import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import api from "../../utilis/api";
import { update } from "../../redux/store/userSlice";

const Address = () => {
  const { user, token } = useSelector((state) => state.user);
  const dispatch = useDispatch();

  // Initialize state from user
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    company: "",
    street_address_1: "",
    street_address_2: "",
    city: "",
    state: "",
    zip_code: "",
    country: "",
  });

  useEffect(() => {
    if (user) {
      const names = user.name.trim().split(" ");
      setFormData({
        firstName: names[0],
        lastName: names.slice(1).join(" "),
        phone: user.phone || "",
        email: user.email || "",
        company: user.company || "",
        street_address_1: user.street_address_1 || "",
        street_address_2: user.street_address_2 || "",
        city: user.city || "",
        state: user.state || "",
        zip_code: user.zip_code || "",
        country: user.country || "",
      });
    }
  }, [user]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Combine firstName + lastName only if user doesn't manually edit full name in DB
    const name =
      formData.firstName && formData.lastName
        ? `${formData.firstName} ${formData.lastName}`
        : user.name;

    const updatedData = {
      name,
      phone: formData.phone,
      email: formData.email,
      company: formData.company,
      street_address_1: formData.street_address_1,
      street_address_2: formData.street_address_2,
      city: formData.city,
      state: formData.state,
      zip_code: formData.zip_code,
      country: formData.country,
    };

    try {
      const res = await api.put(`/auth/update/${user._id}`, updatedData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.meta?.success) {
        toast.success(res.data.meta.message || "Address updated successfully", {
          autoClose: 2000,
        });
        dispatch(update(res.data.data)); // update redux store
      } else {
        toast.error(res.data?.meta?.message || "Update failed", {
          autoClose: 4000,
        });
      }
    } catch (error) {
      const message =
        error?.response?.data?.meta?.message ||
        error.message ||
        "Something went wrong";
      toast.error(message, { autoClose: 5000 });
    }
  };

  return (
    <>
      <Helmet>
        <title>Address Book</title>
        <meta name="description" content={`Welcome to My Account page`} />
      </Helmet>

      <div className="w-full px-4 md:px-8 lg:px-16 py-8">
        <h1 className="text-center font-bold text-[1.6rem] sm:text-[1.7rem] md:text-[1.8rem] mb-10">
          Customer Address
        </h1>

        <form className="w-[100%] md:space-y-12" onSubmit={handleSubmit}>
          {/* ================= Contact Information ================= */}
          <div>
            <h2 className="text-xl font-semibold mb-6 border-b pb-2">
              Contact Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full border text-black rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-black"
              />

              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full border text-black rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-black"
              />

              <input
                type="tel"
                name="phone"
                placeholder="Phone Number"
                value={formData.phone}
                onChange={handleChange}
                className="w-full border text-black rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-black"
              />

              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                className="w-full border text-black rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-black"
              />

              <input
                type="text"
                name="company"
                placeholder="Company"
                value={formData.company}
                onChange={handleChange}
                className="w-full md:col-span-2 text-black border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </div>

          {/* ================= Address Section ================= */}
          <div className="mt-[2rem]">
            <h2 className="text-xl font-semibold mb-6 border-b pb-2">
              Address
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input
                type="text"
                name="street_address_1"
                placeholder="Street Address"
                value={formData.street_address_1}
                onChange={handleChange}
                className="w-full border rounded-lg p-3 text-black placeholder-gray-700 focus:outline-none focus:ring-2 focus:ring-black"
              />

              <input
                type="text"
                name="street_address_2"
                placeholder="Street Address 2"
                value={formData.street_address_2}
                onChange={handleChange}
                className="w-full border rounded-lg p-3 text-black placeholder-gray-700 focus:outline-none focus:ring-2 focus:ring-black"
              />

              <input
                type="text"
                name="city"
                placeholder="City"
                value={formData.city}
                onChange={handleChange}
                className="w-full border rounded-lg p-3 text-black placeholder-gray-700 focus:outline-none focus:ring-2 focus:ring-black"
              />

              <input
                type="text"
                name="state"
                placeholder="Province"
                value={formData.state}
                onChange={handleChange}
                className="w-full border rounded-lg p-3 text-black placeholder-gray-700 focus:outline-none focus:ring-2 focus:ring-black"
              />

              <input
                type="text"
                name="zip_code"
                placeholder="Zip Code"
                value={formData.zip_code}
                onChange={handleChange}
                className="w-full border rounded-lg p-3 text-black placeholder-gray-700 focus:outline-none focus:ring-2 focus:ring-black"
              />

              <input
                type="text"
                name="country"
                placeholder="Country"
                value={formData.country}
                onChange={handleChange}
                className="w-full border rounded-lg p-3 text-black placeholder-gray-700 focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="text-start mt-[1rem]">
            <button
              type="submit"
              className="bg-black text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition-all duration-300"
            >
              Save Address
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Address;
